import { FormattedDate, FormattedTime } from "react-intl";
import { Link as ReactLink } from "react-router-dom";
import { upperFirst, isEmpty } from "./funcs";
import {
  COMMUNITY_STATUS_DELETED,
  COMMUNITY_STATUS_TRIAL,
  MEDIA_PROVIDERTYPE_RSS,
  PLATFORM_TYPE_GOOGLE_TV
} from "@/config/constants";
import androidIconPath from "@/images/emms_platform_icon_android.png";
import androidTVIconPath from "@/images/emms_platform_icon_firetv.png";
import appleTVIconPath from "@/images/emms_platform_icon_appletv.png";
import customAppIconPath from "@/images/emms_platform_icon_custom.png";
import appleIconPath from "@/images/emms_platform_icon_apple.png";
import rokuIconPath from "@/images/emms_platform_icon_roku.png";
import cobraIconPath from "@/images/emms_platform_icon_www.png";
import googleTVIconPath from "@/images/emms_platform_icon_googletv.png";
import protectedIconPath from "@/images/icon_protected.png";
import iconRecordingPath from "@/images/icon_recording.png";
import iconRssPath from "@/images/icon_rss.png";
import iconLivePath from "@/images/icon_live.png";
import WithTooltip from "@/lib/ui/components/WithTooltip";
import iconLiveEventPath from "@/images/icon_live_event.png";
import iconInternalSyndicationPath from "@/images/icon_internal_syndication.png";
import iconClippingPath from "@/images/icon_clipping.png";
import iconMemberPath from "@/images/icon_member.png";
import { NewTabLink } from "@/components/base/index";
import { platformNames } from "@/pages/apps/platforms/service";
import {
  PLATFORM_TYPE_ANDROID,
  PLATFORM_TYPE_ANDROID_TV,
  PLATFORM_TYPE_COBRA,
  PLATFORM_TYPE_CUSTOM,
  PLATFORM_TYPE_IOS,
  PLATFORM_TYPE_ROKU,
  PLATFORM_TYPE_TVOS,
  MEDIA_PROVIDERTYPE_MEMBERUPLOADED,
  MEDIA_PROVIDERTYPE_INTERNAL_SYNDICATION,
  MEDIA_PROVIDERTYPE_RECORDED_EVENT,
  MEDIA_TYPE_LIVE_CHANNEL,
  MEDIA_TYPE_LIVE_EVENT,
  MEDIA_TYPE_VIRTUAL_LIVE_CHANNEL,
  MEDIA_TYPE_VIRTUAL_LIVE_EVENT
} from "@/config/constants";
import { renderFormatIcon, renderStatusIcon } from "./utils";
import { calcImageFitSizes } from "./funcs";
import { semanticButtonStyles } from "@/pages/media/config";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { AutocompleteValueView } from "@/components/base/index";

// this is used in reports for Tooltip label and columns
export const TimeZoneAgnosticDate = ({
  value,
  monthYearOnly,
  fullMonth = false
}) => {
  if (!value) return null;
  let isToday = false;

  if (!monthYearOnly) {
    const date = new window.Date(value);
    const today = new window.Date();
    isToday =
      date.getUTCFullYear() === today.getUTCFullYear() &&
      date.getUTCMonth() === today.getUTCMonth() &&
      date.getUTCDate() === today.getUTCDate();
  }

  return (
    <>
      <FormattedDate
        value={value}
        timeZone={"utc"}
        year={"numeric"}
        weekday={monthYearOnly ? undefined : "long"}
        month={fullMonth ? "long" : "short"}
        day={monthYearOnly ? undefined : "numeric"}
      />
      {isToday ? " (Today)" : ""}
    </>
  );
};

export const Date = ({ value, format = {} }) => {
  if (value) {
    return (
      <FormattedDate
        value={value}
        day={format.day ? format.day : "numeric"}
        month={format.month ? format.month : "short"}
        year={format.year ? format.year : "numeric"}
      />
    );
  }
  return null;
};

export const DateTime = ({
  value,
  format = {},
  useDashOnEmpty = false,
  showSeconds
}) => {
  if (value) {
    return (
      <>
        <FormattedDate
          style={{ display: "inline" }}
          value={value}
          day={format.day ? format.day : "numeric"}
          month={format.month ? format.month : "short"}
          year={format.year ? format.year : "numeric"}
        />
        {" @ "}
        <FormattedTime
          timeZoneName={format?.timeZoneName}
          second={showSeconds ? "numeric" : undefined}
          hour="numeric"
          minute="numeric"
          value={value}
        />
      </>
    );
  }

  if (useDashOnEmpty) {
    return <>-</>;
  }
  return null;
};

export const DatePeriod = ({ startKey = "", endKey = "", rowData = {} }) => {
  const periodStart = rowData[startKey];
  const periodEnd = rowData[endKey];
  if (!periodStart || !periodEnd) return null;
  return (
    <>
      <Date value={periodStart} />
      {" to "}
      <Date value={periodEnd} />
    </>
  );
};

export const Link = ({
  value,
  link = "#",
  rowData,
  subObj = null,
  isExternal = false
}) => {
  const newlink = link.replace(/\$#([\w]+)#/g, (_, match) =>
    subObj ? rowData[subObj][match] : rowData[match]
  );
  if (isExternal) {
    return <a href={newlink}>{value}</a>;
  }
  return <ReactLink to={newlink}>{value}</ReactLink>;
};

export const SubString = ({ value, startPosition, endPosition }) => (
  <p>{value.substring(startPosition, endPosition)}</p>
);

export const UpperFirst = ({ value }) => <p>{upperFirst(value)}</p>;

export const BooleanToText = ({
  value,
  showEmptyOnNull,
  wrapperElement: WrapperElement = "p"
}) => {
  if (showEmptyOnNull && value === null) {
    return "";
  } else return <WrapperElement>{value ? "Yes" : "No"}</WrapperElement>;
};

export const BooleanToTextWithIndeterminate = ({ value }) => (
  <>
    <tt>({value === true ? "x" : "  "})</tt> Yes
    <tt>({value === false ? "x" : "  "})</tt> No
  </>
);

export const Currency = ({ sign = "", value, denomination }) => {
  if (typeof value === "undefined") {
    return null;
  }
  const valueToShow = denomination
    ? (parseInt(value, 10) / denomination).toFixed(2)
    : value;
  return `${sign}${value === "" ? "0.00" : valueToShow}`;
};

export const Percentage = ({ value, fixed = 2 }) => {
  if (typeof value === "undefined") {
    return null;
  }
  return `${parseFloat(value).toFixed(fixed)}%`;
};

export const AccountStatus = ({
  value,
  rowData = {},
  hideTrialInfo = false
}) => {
  const { trialHasExpired, trialExpirationDate } = rowData;
  const isDeletedStatus = value === COMMUNITY_STATUS_DELETED;
  let trialInfo = null;
  if (value === COMMUNITY_STATUS_TRIAL && !hideTrialInfo) {
    trialInfo = trialHasExpired ? (
      "(Expired)"
    ) : (
      <>
        (<Date value={trialExpirationDate} />)
      </>
    );
  }
  return (
    <span className={!isDeletedStatus ? "text-capitalize" : undefined}>
      {isDeletedStatus ? "Scheduled to be Deleted" : value} {trialInfo}
    </span>
  );
};

export const ImageView = ({ value = [], fieldName, className, onDelete }) => {
  const imageData = value.reduce(
    (prev, current) =>
      current?.fieldName === fieldName && current?.width > (prev?.width || 0)
        ? current
        : prev,
    null
  );

  const isRoundImage = className === "rounded-circle";
  const [width, height] = calcImageFitSizes(
    imageData?.width,
    imageData?.height,
    isRoundImage ? 135 : 200,
    isRoundImage ? 135 : 200
  );
  return imageData ? (
    <div className="d-flex flex-column">
      <img
        src={imageData?.url || ""}
        alt={fieldName}
        className={className}
        width={width}
        height={height}
      />
      <span className="">
        {imageData.width}x{imageData.height}
        {onDelete && imageData?.canDelete && (
          <>
            {" "}
            -{" "}
            <button
              className="btn-semantic link"
              style={{ ...semanticButtonStyles, color: "unset" }}
              onClick={() => onDelete(imageData.id)}
            >
              Delete
            </button>
          </>
        )}
      </span>
    </div>
  ) : null;
};

export const PlatformIcon = ({ platform, siteUrl }) => {
  let iconPath = "";
  switch (platform) {
    case PLATFORM_TYPE_ANDROID:
      iconPath = androidIconPath;
      break;
    case PLATFORM_TYPE_ANDROID_TV:
      iconPath = androidTVIconPath;
      break;
    case PLATFORM_TYPE_GOOGLE_TV:
      iconPath = googleTVIconPath;
      break;
    case PLATFORM_TYPE_TVOS:
      iconPath = appleTVIconPath;
      break;
    case PLATFORM_TYPE_CUSTOM:
      iconPath = customAppIconPath;
      break;
    case PLATFORM_TYPE_IOS:
      iconPath = appleIconPath;
      break;
    case PLATFORM_TYPE_ROKU:
      iconPath = rokuIconPath;
      break;
    case PLATFORM_TYPE_COBRA:
      iconPath = cobraIconPath;
      break;
    default:
      iconPath = "";
      break;
  }

  const icon = (
    <img
      src={iconPath}
      alt={platform}
      className="me-2"
      width="24"
      height="24"
    />
  );

  return (
    <WithTooltip
      show={
        platform !== PLATFORM_TYPE_COBRA && Boolean(platformNames?.[platform])
      }
      msg={platformNames?.[platform]}
    >
      {siteUrl ? (
        <NewTabLink href={`https://${siteUrl}`}>{icon}</NewTabLink>
      ) : (
        icon
      )}
    </WithTooltip>
  );
};

export const ImageIconWithTooltip = ({
  iconPath,
  alt,
  tooltipMessage,
  width = 16,
  isSingleIcon,
  overlay
}) => {
  return (
    <WithTooltip overlay={overlay} show={true} msg={tooltipMessage}>
      <img
        src={iconPath}
        alt={alt}
        className={!isSingleIcon ? "me-2" : ""}
        width={width}
      />
    </WithTooltip>
  );
};

export const MediaTypeIcon = ({ media }) => {
  const { isProtected, providerType, mediaType, hasActiveClippingJobs } = media;

  const mediaIcons = [];

  if (isProtected) {
    mediaIcons.push({
      iconPath: protectedIconPath,
      tooltipMessage: "Protected",
      alt: "Protected"
    });
  }

  if (providerType === MEDIA_PROVIDERTYPE_INTERNAL_SYNDICATION) {
    mediaIcons.push({
      iconPath: iconInternalSyndicationPath,
      tooltipMessage: "Internal Syndication",
      alt: "Internal Syndication"
    });
  }

  switch (true) {
    case providerType === MEDIA_PROVIDERTYPE_MEMBERUPLOADED:
      mediaIcons.push({
        iconPath: iconMemberPath,
        tooltipMessage: "Member Upload",
        alt: "Member Upload"
      });
      break;
    case providerType === MEDIA_PROVIDERTYPE_RECORDED_EVENT:
      mediaIcons.push({
        iconPath: iconRecordingPath,
        tooltipMessage: "Live Recording",
        alt: "Live Recording"
      });
      break;
    case mediaType === MEDIA_TYPE_LIVE_CHANNEL:
      mediaIcons.push({
        iconPath: iconLivePath,
        tooltipMessage: "Live Channel",
        alt: "Live Channel"
      });
      break;
    case mediaType === MEDIA_TYPE_LIVE_EVENT:
      mediaIcons.push({
        iconPath: iconLiveEventPath,
        tooltipMessage: "Live Event",
        alt: "Live Event"
      });
      break;
    case mediaType === MEDIA_TYPE_VIRTUAL_LIVE_CHANNEL:
      mediaIcons.push({
        iconPath: iconLivePath,
        tooltipMessage: "Virtual Live Channel",
        alt: "Virtual Live Channel"
      });
      break;
    case mediaType === MEDIA_TYPE_VIRTUAL_LIVE_EVENT:
      mediaIcons.push({
        iconPath: iconLiveEventPath,
        tooltipMessage: "Virtual Live Event",
        alt: "Virtual Live Event"
      });
      break;
    case providerType === MEDIA_PROVIDERTYPE_RSS:
      mediaIcons.push({
        iconPath: iconRssPath,
        tooltipMessage: "MRSS Media",
        alt: "MRSS Media"
      });
      break;
    default:
      break;
  }

  if (hasActiveClippingJobs) {
    mediaIcons.push({
      iconPath: iconClippingPath,
      tooltipMessage:
        "Clipping job in progress or pending. It may take from few seconds to few minutes. Icon will disappear when done. If there is encoding in progress the clipping will start after encoding finishes and the process might take more time. Refresh page to check again.",
      alt: "Clipping job in progress or pending."
    });
  }

  return mediaIcons.length ? (
    <div className="d-inline-flex">
      {mediaIcons.map(({ iconPath, tooltipMessage, alt }, index) => (
        <ImageIconWithTooltip
          key={`${alt + index}`}
          iconPath={iconPath}
          tooltipMessage={tooltipMessage}
          isSingleIcon={
            mediaIcons.length === 1 || index === mediaIcons.length - 1
          }
        />
      ))}
    </div>
  ) : null;
};

export const MediaFormatIcons = ({ media }) => {
  const { renditions, encoderJobs, isInstantlyAvailable } = media;
  return (
    <>
      {renderFormatIcon(renditions, isInstantlyAvailable)}
      {renderStatusIcon(encoderJobs)}
    </>
  );
};

export const CreatedByInViewItemContainer = ({
  createdByName,
  lastUpdatedName,
  createDateTime,
  lastUpdatedDateTime
}) => {
  const hasCreateName = Boolean(createdByName);
  const hasUpdateName = Boolean(lastUpdatedName);
  return (
    <div className="d-flex justify-content-between fst-italic fw-light separatorLine py-2">
      <div className="ms-2">
        {`Created${hasCreateName ? ` By: ${createdByName} ` : ": "}`}
        {`${hasCreateName ? "(" : ""}`}
        <DateTime value={createDateTime} />
        {`${hasCreateName ? ")" : ""}`}
      </div>
      <div className="me-2">
        {`Last Modified${hasUpdateName ? ` By: ${lastUpdatedName} ` : ": "}`}
        {`${hasUpdateName ? "(" : ""}`}
        <DateTime value={lastUpdatedDateTime} />
        {`${hasUpdateName ? ")" : ""}`}
      </div>
    </div>
  );
};

export const NumberWithCommas = ({ value }) =>
  value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const StringWithCopyToClipboard = ({ string, limit }) => {
  const { tooltip, copy, copied } = useCopyToClipboard(string);
  const displayString =
    limit && string.length > limit ? `${string.slice(0, limit)}…` : string;
  return (
    <div className="d-inline-flex align-items-center">
      <span>{displayString}</span>
      <WithTooltip show={true} msg={tooltip}>
        <button
          onClick={copy}
          className="btn-semantic ms-2"
          style={{ ...semanticButtonStyles, color: "unset" }}
        >
          {copied ? (
            <i className="fa-solid fa-check"></i>
          ) : (
            <i className="fa-regular fa-copy"></i>
          )}
        </button>
      </WithTooltip>
    </div>
  );
};

export const StringWithDoubleClickCopy = ({ string }) => {
  const { copy } = useCopyToClipboard(string);
  return <span onDoubleClick={copy}>{string}</span>;
};

export const getFormTitleWithSyndicationIcon = (
  actionWord,
  entityWord,
  item
) => {
  return (
    <div className="d-flex flex-row">
      {actionWord} {entityWord}
      {!isEmpty(item?.internalSyndicationImport) && (
        <span className="ms-2">
          <ImageIconWithTooltip
            iconPath={iconInternalSyndicationPath}
            isSingleIcon
            tooltipMessage={"Internal Syndication"}
          />
        </span>
      )}
    </div>
  );
};

export const ProtectedLink = ({ to, canSee, children }) => (
  <AutocompleteValueView>
    {canSee ? (
      <ReactLink to={to} className="text-decoration-none">
        {children}
      </ReactLink>
    ) : (
      children
    )}
  </AutocompleteValueView>
);

export const moderationStatusValueView = (moderationStatusValue) =>
  moderationStatusValue === "autoApproved"
    ? "Auto-Approved"
    : upperFirst(moderationStatusValue);
